import { css } from "@emotion/react";
import styled from "@emotion/styled";

import {
  alignmentCssVariableValues,
  fontRobotoCondensed,
  theme,
  transientOptions,
  ZIndexLayer,
} from "shared/lib/styles";

import { StyledShareButton } from "scmp-app/components/content/content-share-widget/styles";

import { NewsletterArticleContent } from "./content";
import { NewsletterArticleHeader } from "./header";
import { NewsletterArticleIssues } from "./issues";
import { NewsletterArticleWidget } from "./widget";

export const Outer = styled.div`
  margin-inline: calc(
      (
          ${alignmentCssVariableValues.pageAlignmentPaddingInline} +
            ${alignmentCssVariableValues.contentAlignmentPaddingInline}
        ) *
        -1
    )
    calc(
      (
          ${alignmentCssVariableValues.pageAlignmentPaddingInline} +
            ${alignmentCssVariableValues.contentAlignmentPaddingInline}
        ) *
        -1
    );

  ${theme.breakpoints.down("tablet")} {
    overflow: hidden;
  }
`;

export const Container = styled.div`
  max-inline-size: 876px;
  margin-inline: auto;
  padding-block: 40px 49px;
`;

export const StyledNewsletterArticleHeader = styled(NewsletterArticleHeader)`
  display: flex;
  flex-direction: column;
  align-items: center;

  margin-block-end: 40.5px;
  margin-inline: 0;
  padding-block: 0;
  padding-inline: 20px;

  ${theme.breakpoints.up("tablet")} {
    padding-block: 0;
    padding-inline: 44px;
  }
  ${theme.breakpoints.up("desktop")} {
    padding: 0;
  }
`;

export const Content = styled.div`
  position: relative;
`;

export const IframeContainer = styled.div`
  margin-block-start: -82.5px;
  padding-block: 102.5px 59px;
  padding-inline: 10px;

  border: 1px solid #000000;
  border-width: 1px 0;

  background-color: #f7f7f7;

  ${theme.breakpoints.up("tablet")} {
    margin-block-start: -52.5px;
    padding-block: 73px 60px;
    padding-inline: 98px;
  }
`;

export const Bottom = styled.div`
  padding-block: 46px 0;
  padding-inline: 20px;

  ${theme.breakpoints.up("tablet")} {
    padding-block: 46px 0;
    padding-inline: 98px;
  }
`;

type NewsletterArticleWidgetContainerProps = {
  $isHidden: boolean;
};

export const NewsletterArticleWidgetContainer = styled.div<NewsletterArticleWidgetContainerProps>`
  position: relative;
  z-index: ${ZIndexLayer.NewsletterArticleFloatingWidget};

  margin-block: 0;
  margin-inline: 20px;

  transition:
    transform 0.1s,
    opacity 0.1s;

  ${props =>
    props.$isHidden
      ? css`
          opacity: 0;
          transform: translateY(100%);
        `
      : css`
          opacity: 1;
          transform: translateY(0%);
        `}

  ${theme.breakpoints.up("tablet")} {
    position: sticky;
    inset-block-start: 63px;
  }
`;

export const StyledNewsletterArticleWidget = styled(NewsletterArticleWidget)`
  inline-size: 100%;
  max-inline-size: 720px;
  margin-block: 0;
  margin-inline: auto;
  padding-block: 16px;
  padding-inline: 20px;

  background-color: #ffffff;

  box-shadow:
    0 0 1px 2px rgb(0 0 0 / 0.02),
    2px 3px 10px 0 rgb(0 0 0 / 0.05);
  ${theme.breakpoints.up("tablet")} {
    block-size: 105px;
  }
`;

export const StyledNewsletterArticleContent = styled(NewsletterArticleContent)`
  inline-size: 100%;
  min-block-size: 100vh;

  background-color: #ffffff;
`;

export const StyledNewsletterArticleIssues = styled(NewsletterArticleIssues)`
  position: absolute;
  z-index: ${ZIndexLayer.NewsletterArticleFloatingWidget - 1};

  display: flex;
  justify-content: center;

  inline-size: 100%;

  transform: translateY(-50%);
`;

export const ShareContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  margin-block: 40px 8px;

  ${theme.breakpoints.up("tablet")} {
    margin-block: 60px 20px;
  }
`;

export const CommentTrigger = styled.span`
  flex: 1;

  ${theme.breakpoints.up("tablet")} {
    max-inline-size: 335px;
  }
`;

export const ShareWidget = styled.span`
  display: none;
  ${theme.breakpoints.up("tablet")} {
    display: block;
  }

  ${StyledShareButton} {
    svg {
      > :first-child {
        fill: #001246;
      }

      path {
        fill: #ffffff;
      }
    }
  }
`;

export const AuthorDetail = styled.div`
  margin-block-end: 8px;
`;

export const Topics = styled.div`
  position: relative;
  z-index: ${ZIndexLayer.NewsletterArticleFloatingWidget - 2};

  & > div {
    gap: 0;
  }

  h2 {
    margin-block-end: 8px;
  }

  a {
    font-size: 16px;
    font-family: ${fontRobotoCondensed};
    line-height: 20px;
  }
`;

type Props = {
  $isHidden: boolean;
};

export const StyledNewsletterArticleWidgetBottom = styled(NewsletterArticleWidget, {
  ...transientOptions,
})<Props>`
  position: fixed;
  inset-block-end: 0;
  z-index: ${ZIndexLayer.NewsletterArticleFloatingWidget};

  inline-size: 100%;
  padding-block: 12px;
  padding-inline: 26px;

  background: #ffffff;

  ${props =>
    props.$isHidden
      ? css`
          opacity: 0;
          transform: translateY(100%);
        `
      : css`
          opacity: 1;
          transform: translateY(0%);
        `}

  transition:
    transform 0.1s,
    opacity 0.1s;

  ${theme.breakpoints.up("tablet")} {
    display: none;
  }
`;

export const SpeechPlayer = styled.div`
  grid-area: speech-player;

  margin-block-end: 20px;
  margin-inline: 10px;

  ${theme.breakpoints.up("desktop")} {
    margin-inline: 0;
  }
`;
